import { getAccessToken, getAuthSessionSnapshot } from '@/lib/auth/client'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/v1'

export class ApiError extends Error {
  status: number
  statusText: string
  data: unknown

  constructor(response: Response, data?: unknown) {
    super(`API Error: ${response.status} ${response.statusText}`)
    this.name = 'ApiError'
    this.status = response.status
    this.statusText = response.statusText
    this.data = data
  }
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
  idempotencyKey?: string
  _retryCount?: number
  _skipDedup?: boolean
}

let consecutive401Count = 0

const pendingRequests = new Map<string, Promise<unknown>>()

function generateRequestKey(endpoint: string, options: RequestOptions): string {
  const method = (options.method ?? 'GET').toUpperCase()
  const bodyKey = options.body ? JSON.stringify(options.body) : ''
  return `${method}:${endpoint}:${bodyKey}`
}

async function executeRequest<T>(
  endpoint: string,
  options: RequestOptions,
  token: string | null,
): Promise<T> {
  const { body, idempotencyKey, _retryCount: _, _skipDedup, ...fetchOptions } = options
  const method = (fetchOptions.method ?? 'GET').toUpperCase()
  const shouldAutoIdempotency =
    method === 'POST' &&
    (
      endpoint === '/billing/checkout' ||
      endpoint === '/emporium/shop/purchase' ||
      endpoint === '/emporium/pass/claim' ||
      endpoint === '/emporium/pass/claim-all' ||
      /^\/missions\/[^/]+\/claim$/.test(endpoint) ||
      /^\/habits\/[^/]+\/checkin$/.test(endpoint) ||
      /^\/arena\/challenges\/[^/]+\/checkin$/.test(endpoint) ||
      /^\/arena\/duels\/[^/]+\/accept$/.test(endpoint) ||
      /^\/arena\/duels\/[^/]+\/reject$/.test(endpoint) ||
      /^\/arena\/duels\/[^/]+\/cancel$/.test(endpoint) ||
      /^\/arena\/boss\/[^/]+\/subscribe$/.test(endpoint) ||
      /^\/arena\/boss\/[^/]+\/attack$/.test(endpoint)
    )
  const resolvedIdempotencyKey =
    idempotencyKey ?? (shouldAutoIdempotency ? generateIdempotencyKey() : undefined)

  const headers: HeadersInit = {
    'Content-Type': 'application/json; charset=utf-8',
    ...options.headers,
  }

  if (token) {
    ;(headers as Record<string, string>)['Authorization'] = `Bearer ${token}`
  }

  if (resolvedIdempotencyKey) {
    ;(headers as Record<string, string>)['Idempotency-Key'] = resolvedIdempotencyKey
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...fetchOptions,
    headers,
    credentials: 'include',
    body: body ? JSON.stringify(body) : undefined,
  })

  let data: unknown = null
  if (response.status !== 204 && response.status !== 205) {
    const contentType = response.headers.get('content-type') ?? ''
    if (contentType.includes('application/json')) {
      const rawBody = await response.text()
      data = rawBody.trim().length > 0 ? JSON.parse(rawBody) : null
    } else {
      data = await response.text()
    }
  }

  if (!response.ok) {
    if (response.status === 429) {
      console.warn('[api-client] Rate limit hit (429). Slow down requests to', endpoint)
    }
    throw new ApiError(response, data)
  }

  consecutive401Count = 0
  return data as T
}

async function executeWithDedup<T>(
  endpoint: string,
  options: RequestOptions,
  token: string | null,
): Promise<T> {
  const skipDedup = options._skipDedup ?? false
  
  if (skipDedup || options.method === 'POST' || options.method === 'PATCH' || options.method === 'PUT' || options.method === 'DELETE') {
    return executeRequest<T>(endpoint, options, token)
  }

  const requestKey = generateRequestKey(endpoint, options)
  const existingRequest = pendingRequests.get(requestKey)

  if (existingRequest) {
    return existingRequest as Promise<T>
  }

  const requestPromise = executeRequest<T>(endpoint, options, token)
    .then((result) => {
      pendingRequests.delete(requestKey)
      return result
    })
    .catch((error) => {
      pendingRequests.delete(requestKey)
      throw error
    })

  pendingRequests.set(requestKey, requestPromise)
  return requestPromise
}

export async function apiClient<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const token = await getAccessToken()
  const retryCount = options._retryCount ?? 0

  try {
    return await executeWithDedup<T>(endpoint, options, token)
  } catch (error) {
    if (
      error instanceof ApiError &&
      error.status === 401 &&
      retryCount === 0 &&
      !endpoint.startsWith('/auth/')
    ) {
      consecutive401Count++

      if (consecutive401Count >= 2) {
        consecutive401Count = 0
        const { createAuthClient } = await import('@/lib/auth/client')
        const client = createAuthClient()
        await client.auth.signOut()
        throw error
      }

      const freshToken = await getAccessToken()
      if (freshToken && freshToken !== token) {
        return apiClient<T>(endpoint, { ...options, _retryCount: 1 })
      }
    }

    throw error
  }
}

export function clearPendingRequests(): void {
  pendingRequests.clear()
}

export function getPendingRequestsCount(): number {
  return pendingRequests.size
}

export const api = {
  get: <T>(endpoint: string, options?: RequestOptions) =>
    apiClient<T>(endpoint, { ...options, method: 'GET' }),

  post: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiClient<T>(endpoint, { ...options, method: 'POST', body }),

  patch: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiClient<T>(endpoint, { ...options, method: 'PATCH', body }),

  put: <T>(endpoint: string, body?: unknown, options?: RequestOptions) =>
    apiClient<T>(endpoint, { ...options, method: 'PUT', body }),

  delete: <T>(endpoint: string, options?: RequestOptions) =>
    apiClient<T>(endpoint, { ...options, method: 'DELETE' }),
}

export function generateIdempotencyKey(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`
}
